/**
 * Live-trading one-click placement (live-tennis V1b).
 *
 * An explicitly ARMED interface state in which a ladder/pill price click on a
 * Kalshi market submits a limit order immediately — no ticket, no second
 * click. Design rules, in order of importance:
 *
 *   • Arming is SESSION-ONLY. It always starts disarmed and is never
 *     persisted. Refresh, access loss, identity change, route departure,
 *     board-context change, venue departure, and the toggle disarm it.
 *   • The one-click size is its own explicit setting (persisted), NOT the
 *     ticket's `defaultContracts`: that pref is deliberately blank-fill-only
 *     in the panel, and reusing it here would give the panel's anti-fat-finger
 *     rule a second meaning. Hard-clamped to ONE_CLICK_MAX_CONTRACTS — the
 *     largest explicit per-trader ceiling the relay accepts.
 *   • Submissions are paced by MIN_ORDER_SPACING_MS (the same ≤4/sec spacing
 *     the multi-leg ladder uses on the shared org Kalshi token bucket). A
 *     click landing inside the window is DROPPED and surfaced as "throttled",
 *     never queued — a queue would fire orders after the market moved.
 *   • Every order carries a fresh `lt-` client_order_id so the relay audit
 *     rows (and Kalshi's echo) can be joined back to one-click inputs during
 *     post-session reconciliation.
 *   • postOnly rides the click's ladder semantics: a bid-level click joins
 *     (post_only=true, can't cross), an ask-level click may cross. This store
 *     never overrides what the click site derived.
 *
 * The submit path is accountStore.placeOrder — the same audited relay lane
 * (checkOrderLimit → trades audit row → trackIntent) every manual ticket
 * order takes. There is no second order route.
 */

import { create } from 'zustand';
import {
  hasPendingKalshiPlacementConfirmation,
  useAccountStore,
} from './accountStore';
// Type-only: authStore sits in a wide import cycle, and a value import from
// here reorders module init enough to break the board (#1067). The caller,
// which already reads the user, passes the ceiling in.
import type { AuthUser } from './authStore';
import { MIN_ORDER_SPACING_MS } from '../views/ValueDashboard/orderLadder';
import type { VenueBook } from '../views/ValueDashboard/types';
import type { OneClickReceipt, OneClickReceiptsResponse } from '../types/kalshi';
import { OrderSubmissionUnknownError, selfTradePartialMessage } from '../lib/trading/orderSubmissionOutcome';
import {
  buildLt2AuditMeta,
  lt2AuditRequired,
  evaluateIntentGuard,
  intentPriceRefusal,
  type LiveOrderIntent,
} from '../lib/trading/liveIntent';
import { recordLt2Timing, updateLt2Timing, lt2ClockStamp } from '../lib/trading/lt2Timing';
import { armFillWatch, fillText, fillWatchSeen, pressScope, pressScopeCurrent, type OneClickFillResult } from './oneClickFillWatch';
import { sourceConfirmationError } from '../lib/trading/sourceConfirmation';
import { deriveAttackTarget } from '../lib/trading/attackTarget';
import { getChannelVolume, isChannelSoundEnabled } from '../lib/alerts/alertPreferences';
import { resolvePressDuration, type OneClickDuration } from '../lib/trading/oneClickExpiry';
import { playOrderAckTone, playOrderRejectedTone } from '../lib/audio/tradeAudio';

/** Largest explicit per-trader one-click ceiling supported by the relay. */
export const ONE_CLICK_MAX_CONTRACTS = 2_147_483_647;
/** Conservative V1 fallback when no explicit per-trader ceiling is present. */
export const ONE_CLICK_FALLBACK_MAX_CONTRACTS = 500;
export const ONE_CLICK_DEFAULT_CONTRACTS = 100;

/**
 * The largest per-order size this trader's one-click may send: their explicit
 * server ceiling (traderRules.oneClickMaxContracts, D-020 LT2-010) when one is
 * present, else the conservative V1 fallback. The relay enforces its own copy
 * on every order; this is the client's matching bound, so a size it would
 * refuse is refused here first, with the number named.
 */
export function oneClickCeiling(
  user: Pick<AuthUser, 'oneClickMaxContracts'> | null | undefined,
): number {
  const serverCeiling = user?.oneClickMaxContracts ?? null;
  return serverCeiling != null
    ? Math.min(ONE_CLICK_MAX_CONTRACTS, serverCeiling)
    : ONE_CLICK_FALLBACK_MAX_CONTRACTS;
}

const SIZE_STORAGE_KEY = 'galactus:oneClickSizeContracts';

export interface OneClickAck {
  /** Monotonic per-session sequence so the UI can key transitions. */
  seq: number;
  /** 'unverified' (LT2-009): the venue accepted a resting order but it was
   *  no longer visible in the open-orders view 5s later — reconcile via
   *  receipts rather than assuming it is live OR dead.
   *  'partial' (#724): a two-sided press where one leg was venue-accepted and
   *  the other was not. Never collapsed into 'accepted' — the accepted leg is
   *  a live resting order the trader must now manage alone. */
  status: 'pending' | 'accepted' | 'rejected' | 'throttled' | 'unverified' | 'partial';
  /** e.g. "BUY 100 NO @ 24¢ SVITOLINA" — built by the click site. */
  label: string;
  message?: string;
  atMs: number;
  /** #724 — one line per leg of a two-sided press, in declared visible order
   *  (top/left first). Absent on every single-order ack. The strip renders
   *  each leg beneath the summary so a refused side is always visible.
   *  D13 — an earlier press's late result rides here too, after the legs,
   *  marked `earlier`. */
  legs?: OneClickLegAck[];
  /** D13 — the press that owns this line. Its own later results replace the
   *  line; any other press's late result is added beneath it instead. */
  press?: number;
  /** #1025 — what this press's order actually filled, from its fill rows.
   *  Absent until a fill arrives; never derived from the sent price. */
  fill?: OneClickFillResult;
}

/** One leg of a two-sided (S) acknowledgement (#724), or (D13) a late result
 *  of an earlier press, added beneath a newer line. */
export interface OneClickLegAck {
  /** The sized leg label, e.g. "100× DAL −3.5 YES @ 47¢". */
  label: string;
  status: 'pending' | 'accepted' | 'rejected' | 'unverified';
  message?: string;
  /** D13 — an earlier press's result, not part of the press on the headline.
   *  It stays until the next press starts a new line. */
  earlier?: true;
}

/** The most recent venue-accepted one-click placement — the reconciliation
 *  anchor for the 'unverified' check and any receipt deep-link. */
export interface OneClickLastAccepted {
  clientOrderId: string;
  venueOrderId: string | null;
  ticker: string;
  side: 'yes' | 'no';
  priceCents: number;
  atMs: number;
}

export interface OneClickSubmitInput {
  ticker: string;
  side: 'yes' | 'no';
  priceCents: number;
  postOnly: boolean;
  /** Short human label for the ack line (team/player + side). */
  label: string;
}

interface OneClickStore {
  armed: boolean;
  sizeContracts: number;
  /** The strip's line. D5 — kept through a disarm, so a result that lands
   *  after 1-Click off is not lost; see {@link ackAfterDisarm}. */
  lastAck: OneClickAck | null;
  /** D5 — the strip's write count at the last disarm. A `lastAck` with a
   *  higher `seq` was written after it. */
  disarmSeq: number;
  /** B6 — Attack was armed when a board-driven disarm (one with a reason,
   *  not manual) turned one-click off. The next arm() brings Attack back
   *  once (attackStore does it); the trader's own off, or Attack turned off
   *  in between, clears it. */
  attackRestore: boolean;
  lastAccepted: OneClickLastAccepted | null;
  arm: () => void;
  disarmReason: string | null;
  /**
   * Turn one-click off. `reason` says why on the strip when the trader did
   * not do it themselves (a board change, a market that needs the ticket).
   * D4 — `manual: true` is the trader's own off (the 1-Click toggle, or
   * losing one-click access): only it also clears an S hold. Every other
   * disarm, reasonless or not, leaves the hold in place.
   */
  disarm: (reason?: string, opts?: { manual?: boolean }) => void;
  setSizeContracts: (n: number) => void;
  /** Fire a one-click order. No-op (with a truthful ack) when disarmed,
   *  throttled, or the input fails the local guards.
   *
   *  LEGACY V1 shape with no remaining production callsite: every armed
   *  mouse click routes through the intercept adapters into `submitIntent`,
   *  which is where the acknowledgement/timing parity contract lives. Kept
   *  for its pinned tests and as the documented V1 input; new callers use
   *  `submitIntent`. */
  submit: (input: OneClickSubmitInput) => Promise<void>;
  /** D-020 path: fire a normalized LT2 intent through the SUBMIT-TIME guard
   *  (identity, availability, price, maker-role revalidation against the
   *  current book) before the audited placement. Every refusal surfaces a
   *  truthful ack — never a silent drop while armed. */
  submitIntent: (
    intent: LiveOrderIntent,
    book: VenueBook | null | undefined,
    report?: PlacementReport,
  ) => Promise<void>;
  /** Truthful local refusal on the ack line for input paths that fail before
   *  an intent can even be built (hotkey with no displayed price / target).
   *  Never a silent no-op while armed. */
  rejectLocal: (label: string, message: string) => void;
  /** V3 Spacebar: post a non-order lifecycle ack (Pull All pending/summary)
   *  onto the same strip. Order intents keep their own paths. Tones: a new
   *  'pending' press rings the neutral tick (requests are leaving); 'partial'
   *  (D2: not everything was cancelled) and 'rejected' ring the descent. */
  noteAck: (
    status: 'pending' | 'accepted' | 'partial' | 'rejected',
    label: string,
    message?: string,
    /** D13 — the press this state belongs to (the id an earlier call
     *  returned). Absent starts a new press. A state for a press that no
     *  longer owns the strip is added beneath the newer line. */
    press?: number,
  ) => number;
  /** Live Trading V3 W/E: fire a bounded ATTACK intent — the same guarded,
   *  audited lane as submitIntent, with the attack size, the `__attack` gate
   *  marker, and postOnly false (intentional taker within the bound). Refuses
   *  truthfully when attack is not armed. */
  submitAttackIntent: (
    intent: LiveOrderIntent,
    book: VenueBook | null | undefined,
    report?: PlacementReport,
  ) => Promise<void>;
  /** #724 two-sided S: fire the focused card's two PASSIVE legs (exactly the
   *  A and D intents, in that order) as one press. All-or-none PREFLIGHT —
   *  both legs pass the submit-time guard before either is sent — then both
   *  are placed concurrently through the same audited lane as submitIntent,
   *  each at the one-click size, each with its own `lt-` coid and the shared
   *  group stamp. Completion is NOT atomic: outcomes are reported per leg and
   *  summarized truthfully (accepted / partial / rejected / unverified); an
   *  accepted leg beside a refused one is never rolled back, hedged or
   *  resent. One press = one pacing window. */
  submitTwoSidedIntent: (args: {
    /** The group's ack label, e.g. "S DAL −3.5". */
    label: string;
    legs: readonly [LiveOrderIntent, LiveOrderIntent];
    books: readonly [VenueBook | null | undefined, VenueBook | null | undefined];
    report?: PlacementReport;
    /** #1067 — Shift+S: each leg is this many times the one-click size. A
     *  quantity that is not a whole number within the trader's one-click
     *  ceiling refuses the press, named; it is never clamped. Absent = S. */
    sizeMultiplier?: number;
    /** The trader's one-click ceiling (`oneClickCeiling(user)`), required with
     *  `sizeMultiplier`: the multiplied quantity must not exceed it. */
    ceilingContracts?: number;
  }) => Promise<void>;
  /** #724 — an earlier S press with at least one side whose response was
   *  lost, so nobody knows whether it was placed. While it is set, the next S
   *  press is refused: resending both legs could double a side that is
   *  already live. An ACCEPTED leg that is not yet visible does not hold — it
   *  is known to exist at the venue. A, D, W and E are unaffected. Cleared
   *  when every lost side shows up in open orders or (B9) its one-click
   *  receipt shows a known state, by `releaseTwoSidedHold`, or by a manual
   *  disarm (D4). Board-driven disarms keep it. */
  twoSidedHold: TwoSidedHold | null;
  /** Reconcile the hold against current account state, then return the
   *  refusal for a new S press, or null when S may fire. A refusal also asks
   *  the receipts again at once (B9). */
  twoSidedHoldReason: () => string | null;
  /** D15 — capabilities the relay has refused for this session, by code:
   *  LT2_TWO_SIDED_DISABLED (S) and PULL_ALL_DISABLED (Spacebar Pull All).
   *  The readout stops naming those keys and a press is refused here, named,
   *  without a round trip. A reload clears it. */
  deskOff: { twoSided: boolean; pullAll: boolean };
  /** D15 — react to a relay capability code: one-click or Live Trading V2
   *  turned off for the account disarms with that reason (re-reading the
   *  profile, never restoring Attack); Attack turned off disarms Attack; a
   *  desk switch marks `deskOff`. Any other code changes nothing. */
  noteCapabilityRefusal: (code: string) => void;
  /** B9 — "I checked, release S": the trader has looked at open orders and
   *  takes responsibility for the lost side. Clears the hold; one-click,
   *  Attack and the aim stay as they are. */
  releaseTwoSidedHold: () => void;
}

/**
 * What one press put on the wire, told to the caller that fired it: each
 * accepted order's venue id (null when the venue answered without one), and
 * each order whose response was lost. Nothing is reported for a press that
 * never left the browser (refused, throttled, disarmed). The hotkey listener
 * uses it so Pull All can sweep exactly this press's orders if they land
 * after the pull's first pass.
 */
export interface PlacementReport {
  /** An accepted order: its venue id (null when the venue answered without
   *  one) and the market it was placed on. */
  placed: (venueOrderId: string | null, ticker: string) => void;
  lost: () => void;
}

/**
 * D5 — the strip line written AFTER the last disarm, if any: a Pull All or
 * an order whose result came back once one-click was already off. The status
 * line shows it beside "One-click off" so a kill-reflex disarm never hides a
 * result such as "2 NOT cancelled". Null while armed.
 */
export function ackAfterDisarm(state: {
  armed: boolean;
  lastAck: OneClickAck | null;
  disarmSeq: number;
}): OneClickAck | null {
  if (state.armed || state.lastAck == null) return null;
  return state.lastAck.seq > state.disarmSeq ? state.lastAck : null;
}

/** D5 — a line re-arming leaves on the strip: anything not fully placed (a
 *  refusal, a pacing drop, an unknown or partial result, a press still in
 *  flight, or an earlier line beneath that is one of those). */
function ackSurvivesArm(ack: OneClickAck | null): boolean {
  if (ack == null) return false;
  if (ack.status !== 'accepted') return true;
  return (ack.legs ?? []).some((leg) => leg.status !== 'accepted');
}

/** The lost sides of the S press that set the hold, by their coids. */
export interface TwoSidedHold {
  /** The group's ack label, e.g. "S DAL/NYG ML". */
  label: string;
  legs: ReadonlyArray<{ label: string; clientOrderId: string; ticker: string }>;
}

/**
 * B9 — reads one ticker's one-click receipts for the hold check. Null means
 * the read failed, and nothing is concluded from it.
 */
type TwoSidedReceiptReader = (
  ticker: string,
) => Promise<ReadonlyArray<Pick<OneClickReceipt, 'clientOrderId' | 'state'>> | null>;

async function readReceiptsFromRelay(
  ticker: string,
): Promise<ReadonlyArray<Pick<OneClickReceipt, 'clientOrderId' | 'state'>> | null> {
  try {
    // Imported at call time: authStore sits in a wide import cycle, and a
    // module-level value import from here reorders init enough to break the
    // board (#1067). Holds happen long after init.
    const { apiFetch } = await import('./authStore');
    const res = await apiFetch(
      `/api/trader/one-click-receipts?${new URLSearchParams({ ticker }).toString()}`,
    );
    if (!res.ok) return null;
    const body = (await res.json()) as OneClickReceiptsResponse;
    return Array.isArray(body?.receipts) ? body.receipts : null;
  } catch {
    return null;
  }
}

let _receiptReader: TwoSidedReceiptReader = readReceiptsFromRelay;

/** Test seam for the B9 hold check's receipt read; null restores the relay. */
export function _setTwoSidedReceiptReaderForTests(fn: TwoSidedReceiptReader | null): void {
  _receiptReader = fn ?? readReceiptsFromRelay;
}

/** B9 — when the S hold asks the lost leg's receipts, after the hold is set:
 *  quickly at first (a lost response usually lands in the audit within
 *  seconds), then every 30 s while the hold lasts. */
export const TWO_SIDED_HOLD_CHECK_MS = [2_000, 5_000, 15_000, 30_000] as const;

/**
 * The ack line's frozen attempt identity: "<count>× <label>", e.g.
 * "100× DAL -3.5 YES @ 48¢". The count is the size actually sent, captured
 * once at entry and never re-read from the size input after dispatch, so
 * pending, accepted, rejected and unverified all name the same order even if
 * the trader edits the size mid-flight. The label is untouched.
 */
export function sizedAckLabel(label: string, count: number): string {
  return `${count}× ${label}`;
}

/**
 * Whole contracts within the hard maximum, or null for a non-finite input.
 * Null is a refusal, never a default: substituting the default here once let
 * a backspaced size box (NaN) become a live 100-lot, and the next digits typed
 * after it clamp to the ceiling. A setter that gets null keeps the size it
 * had; a submit path that gets null sends nothing.
 */
function clampSize(n: number): number | null {
  if (!Number.isFinite(n)) return null;
  return Math.max(1, Math.min(ONE_CLICK_MAX_CONTRACTS, Math.round(n)));
}

function loadSize(): number {
  try {
    const raw = localStorage.getItem(SIZE_STORAGE_KEY);
    // Loading keeps its default: nothing stored, or nothing readable, starts
    // the session at the default size, as it always has.
    return (raw == null ? null : clampSize(parseInt(raw, 10))) ?? ONE_CLICK_DEFAULT_CONTRACTS;
  } catch {
    return ONE_CLICK_DEFAULT_CONTRACTS;
  }
}

/** D14 — a press dropped by pacing names the order it did not send. Every
 *  dropped press also rings the refusal tone: it was not sent. */
function pacingMessage(label: string): string {
  return `Ignored ${label} — pacing (≤4/sec), press again`;
}

/** The refusal for a submit that finds no usable size in the store. */
const SIZE_NOT_SET = 'One-click size is not set — nothing sent';

/**
 * The order fields a resolved duration adds: the ticket's ISO
 * `expirationTime` for GTS / GTD (accountStore turns it into `expiration_ts`),
 * nothing for GTC. A GTS press also names the start it expires at
 * (`goodToStart`), the audit stamp the relay's start watcher keys on; a GTD
 * (Custom) press is a chosen time, never watched. Only ever called with a
 * duration that resolved.
 */
function expiryFields(
  duration: OneClickDuration,
): { expirationTime?: string; goodToStart?: { startMs: number } } {
  if (!duration.ok || duration.kind === 'gtc') return {};
  return duration.kind === 'gts'
    ? { expirationTime: duration.expirationTime, goodToStart: { startMs: duration.expirationMs } }
    : { expirationTime: duration.expirationTime };
}

/** The strip's reason for a press refused by the submit-time guard or, once
 *  that passed, by Good to Start. */
function refusalMessage(
  guard: ReturnType<typeof evaluateIntentGuard>,
  duration: OneClickDuration | null,
): string {
  if (!guard.ok) return guard.message;
  return duration != null && !duration.ok ? duration.message : 'Order not sent';
}

let _ackSeq = 0;
/** D13 — one id per press; the press that owns the strip's headline. */
let _pressSeq = 0;
let _lastSubmitAtMs = 0;

/** D13 — at most this many earlier-press lines ride beneath one headline. */
const MAX_EARLIER_NOTES = 3;

type AckPartial = Omit<OneClickAck, 'seq' | 'atMs' | 'press'>;

/**
 * D13 — an earlier press's result, as the line added beneath a newer
 * headline. A pending state is not a result and adds nothing.
 */
function earlierNote(partial: AckPartial): OneClickLegAck | null {
  if (partial.status === 'pending') return null;
  const status: OneClickLegAck['status'] =
    partial.status === 'accepted'
      ? 'accepted'
      : partial.status === 'rejected' || partial.status === 'throttled'
        ? 'rejected'
        : 'unverified';
  const label =
    partial.status === 'accepted' && partial.fill != null
      ? `earlier: ${fillText(partial.fill)} — sent ${partial.label}`
      : partial.status === 'accepted' && partial.message == null
        ? `earlier: ${partial.label} placed`
        : `earlier: ${partial.label}`;
  return {
    label,
    status,
    ...(partial.message != null ? { message: partial.message } : {}),
    earlier: true,
  };
}

/** Registered by attackStore at ITS module init — a lazy accessor instead of
 *  an import, because attackStore already imports this store (for the arm
 *  subordination) and a top-level cycle left this module's exports
 *  uninitialized in attackStore's body. Null = attack machinery not loaded,
 *  which fails closed below exactly like a disarmed attack. */
let _attackState:
  | (() => { attackArmed: boolean; attackSizeContracts: number; disarmAttack?: () => void })
  | null = null;
/**
 * The LOCAL acknowledgement sound — rings the moment a valid intent passes
 * local preflight, for mouse and hotkey alike, which is the parity contract:
 * the first audible feedback must not wait for the relay round trip. It is
 * its OWN channel ('orderAck', a neutral tick) precisely because it fires
 * before the venue answers: the venue-accepted chirp ('orderPlaced') keeps
 * its registry meaning and still rings from placeOrder on success, and the
 * rejection descent stays the failure sound. Three states, three sounds,
 * each independently toggleable.
 */
function playLocalAckTone(): void {
  if (isChannelSoundEnabled('orderAck')) playOrderAckTone(getChannelVolume('orderAck'));
}

/** Audible local refusal — an armed trader's refused keypress must not be
 *  silent-to-the-ear when a click would have rung (perceived input lag).
 *  Shares the 'orderRejected' channel, whose registry description covers
 *  local guard refusals and venue rejections alike. Exported for the size
 *  boxes, whose refused size is the same kind of local refusal. */
export function playLocalRefusalTone(): void {
  if (isChannelSoundEnabled('orderRejected')) {
    playOrderRejectedTone(getChannelVolume('orderRejected'));
  }
}

export function _registerAttackStateAccessor(
  fn: () => { attackArmed: boolean; attackSizeContracts: number; disarmAttack?: () => void },
): void {
  _attackState = fn;
}

/** D15 — the relay's machine code on a definite refusal, if it sent one
 *  (OrderRefusedError.code). Read by shape: the error class lives in
 *  accountStore, which tests replace wholesale. */
function refusalCode(err: unknown): string | undefined {
  const code = (err as { code?: unknown } | null)?.code;
  return typeof code === 'string' && code !== '' ? code : undefined;
}

/** D15 — the strip line for an attack the relay refused because Attack was
 *  turned off for the account. */
const ATTACK_OFF_MESSAGE = 'Attack was turned off for your account';

/** D15 — re-read the profile after the relay says a grant changed, so every
 *  control converges on the new access. Imported at call time (#1067). */
async function refreshProfile(): Promise<void> {
  try {
    const { useAuthStore } = await import('./authStore');
    await useAuthStore.getState().refreshMe();
  } catch {
    /* the next profile read converges it */
  }
}

/** D39 — where the strip sends a trader to look. Receipts render on neither
 *  the board nor the other moneyline surface; open orders (the All trades
 *  page) exist everywhere one-click does. */
export const CHECK_OPEN_ORDERS = 'check open orders (All trades)';

/** The amber line for an accepted resting order that is not on the book 5 s
 *  later (LT2-009). */
const NOT_ON_BOOK_MESSAGE = `Accepted but not on the book — ${CHECK_OPEN_ORDERS}`;

/** How long after venue acceptance a claimed-resting order may stay invisible
 *  in the open-orders view before the ack flips to 'unverified' (LT2-009). */
export const ONE_CLICK_VERIFY_AFTER_MS = 5_000;

/**
 * B3 — how long a one-click or hotkey order waits for the relay's answer
 * before the press is reported as unknown. With no deadline a hung request
 * kept the hotkey gate shut and the strip on "Placing…" indefinitely; after
 * it, the press reads exactly as a lost response does (amber, no tone, may be
 * live) and the gate opens. The request itself is not abandoned: a late
 * answer still updates the strip.
 */
export const ONE_CLICK_RESPONSE_DEADLINE_MS = 5_000;

const DEADLINE_MESSAGE =
  'No answer within 5 s — result unknown; it may be live. Check open orders before placing again.';

/** B3 — the strip's word for an acceptance that arrived after the deadline. */
const LATE_ACCEPTED_MESSAGE = 'placed (answered after 5 s)';

/**
 * B3 — `placement`, or an unknown-result error once the deadline passes.
 * An answer that arrives after the deadline goes to `onLate`, scheduled as a
 * task so the caller's unknown line is always written first.
 */
function placeWithDeadline<T>(
  placement: Promise<T>,
  onLate: (late: PromiseSettledResult<T>) => void,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      reject(new OrderSubmissionUnknownError(DEADLINE_MESSAGE));
    }, ONE_CLICK_RESPONSE_DEADLINE_MS);
    placement.then(
      (value) => {
        if (timedOut) setTimeout(() => onLate({ status: 'fulfilled', value }), 0);
        else {
          clearTimeout(timer);
          resolve(value);
        }
      },
      (reason: unknown) => {
        if (timedOut) setTimeout(() => onLate({ status: 'rejected', reason }), 0);
        else {
          clearTimeout(timer);
          reject(reason);
        }
      },
    );
  });
}

/**
 * LT2-009's evidence question, asked once per accepted resting order: is the
 * venue order visible in authoritative state? Resting is visible; a fill is
 * also a fully accounted-for exit. The placement fence temporarily re-injects
 * an accepted order into openOrders while venue reads lag; that projection
 * must not prove itself visible.
 */
function restingOrderVisible(venueOrderId: string): boolean {
  const account = useAccountStore.getState();
  const venueOrderVisible =
    !hasPendingKalshiPlacementConfirmation(venueOrderId) &&
    (account.openOrders ?? []).some((o) => o.orderId === venueOrderId);
  return (
    venueOrderVisible
    || (account.recentFills ?? []).some((f) => f.orderId === venueOrderId)
    // #1025: a fill seen for it is evidence too, after its row left the window.
    || fillWatchSeen(venueOrderId)
  );
}

/**
 * Has an S leg whose response was lost shown up in account state? It has no
 * venue id, so it is matched by its own `lt-` coid, which the venue echoes on
 * open-order reads. A leg that never appears keeps the hold until the trader
 * disarms: absence is not proof it was never placed (it may have filled).
 */
function twoSidedLegSeen(leg: { clientOrderId: string }): boolean {
  return (useAccountStore.getState().openOrders ?? []).some(
    (o) => o.clientOrderId === leg.clientOrderId,
  );
}

/** Pure: does this hold still refuse S right now? (No state change; the
 *  header readout asks it on render.) */
export function twoSidedHoldBlocks(hold: TwoSidedHold | null): boolean {
  return hold != null && !hold.legs.every(twoSidedLegSeen);
}

/** Per-leg placement result of a two-sided press, before summarization. */
type TwoSidedLegResult =
  | { status: 'accepted'; venueOrderId: string | null; message?: string }
  | { status: 'rejected'; message: string }
  | { status: 'unverified'; message: string };

/**
 * The group verdict from its legs' verdicts (#724). `unverified` outranks
 * everything because a lost response is the one outcome the trader cannot act
 * on without looking; `partial` is its own word because "placed" would be a
 * lie about the refused side and "rejected" a lie about the resting one.
 */
export function summarizeTwoSided(
  results: readonly TwoSidedLegResult[],
): 'accepted' | 'partial' | 'rejected' | 'unverified' {
  if (results.some((r) => r.status === 'unverified')) return 'unverified';
  if (results.every((r) => r.status === 'accepted')) return 'accepted';
  if (results.some((r) => r.status === 'accepted')) return 'partial';
  return 'rejected';
}

export const useOneClickStore = create<OneClickStore>((set, get) => {
  /**
   * D13 — a new press's first line (pending, or a refusal before anything was
   * sent). It replaces the strip, earlier-press lines included: the strip
   * answers the latest press, and older lines persist only until the next.
   */
  const startPress = (partial: AckPartial): number => {
    const press = ++_pressSeq;
    set({ lastAck: { ...partial, seq: ++_ackSeq, press, atMs: Date.now() } });
    return press;
  };

  /** #1278 — an answer from an older session writes nothing, so its press's
   *  own pending line would never resolve: drop it, and only it. */
  const dropPendingLine = (press: number): void => {
    const current = get().lastAck;
    if (current?.press === press && current.status === 'pending') set({ lastAck: null });
  };

  /**
   * D13 — a later state of `press`. While that press owns the strip it
   * replaces the headline, keeping any earlier-press lines beneath it. Once a
   * newer press has spoken, the result is ADDED beneath the newer line as an
   * "earlier:" line instead: an older outcome never erases a newer one.
   * Returns whether the press still owned the strip.
   */
  const updatePress = (press: number, partial: AckPartial): boolean => {
    const current = get().lastAck;
    if (current == null || current.press === press) {
      const legs = [...(partial.legs ?? []), ...(current?.legs ?? []).filter((l) => l.earlier)];
      const { legs: _ownLegs, ...rest } = partial;
      set({
        lastAck: {
          ...rest,
          ...(legs.length > 0 ? { legs } : {}),
          seq: ++_ackSeq,
          press,
          atMs: Date.now(),
        },
      });
      return true;
    }
    const note = earlierNote(partial);
    if (note != null) {
      const own = (current.legs ?? []).filter((l) => !l.earlier);
      const notes = [...(current.legs ?? []).filter((l) => l.earlier), note].slice(
        -MAX_EARLIER_NOTES,
      );
      set({ lastAck: { ...current, legs: [...own, ...notes], seq: ++_ackSeq } });
    }
    return false;
  };

  /**
   * B3 — a single order's answer that arrived after its 5 s deadline. The
   * press's unknown line is replaced while it still owns the strip, or the
   * answer is added beneath a newer line (D13). A late "unknown" adds
   * nothing: the line already says so.
   */
  /**
   * #1025 — watch an accepted press's order for its fills. While the press
   * owns the strip every change rewrites its line; once a newer press has
   * spoken, only the completed result is added beneath it (one line, not one
   * per partial — partials are the log's job).
   */
  const watchFills = (
    press: number,
    label: string,
    venueOrderId: string,
    side: 'yes' | 'no',
    count: number,
    message?: string,
    selfTradeFilled?: number,
  ): void => {
    armFillWatch(
      { press, venueOrderId, side, count, ...(selfTradeFilled != null ? { selfTradeFilled } : {}) },
      (owner, fill, done) => {
        if (get().lastAck?.press !== owner && !done) return;
        updatePress(owner, { status: 'accepted', label, fill, ...(message != null ? { message } : {}) });
      },
    );
  };

  const noteLateResult = (
    press: number,
    scope: string,
    label: string,
    intent: LiveOrderIntent,
    clientOrderId: string,
    count: number,
    late: PromiseSettledResult<{ orderId?: unknown } | null | undefined>,
  ): void => {
    // #1278: an answer from before a sign-in or account change belongs to the
    // old session; it writes no line and flips no desk flag in the new one.
    if (!pressScopeCurrent(scope)) return;
    if (late.status === 'fulfilled') {
      updatePress(press, { status: 'accepted', label, message: LATE_ACCEPTED_MESSAGE });
      const orderId = late.value?.orderId;
      if (typeof orderId === 'string' && orderId !== '') {
        watchFills(press, label, orderId, intent.side, count, LATE_ACCEPTED_MESSAGE);
      }
      set({
        lastAccepted: {
          clientOrderId,
          venueOrderId: typeof orderId === 'string' && orderId !== '' ? orderId : null,
          ticker: intent.ticker,
          side: intent.side,
          priceCents: intent.priceCents,
          atMs: Date.now(),
        },
      });
      return;
    }
    if (late.reason instanceof OrderSubmissionUnknownError) return;
    const code = refusalCode(late.reason);
    updatePress(press, {
      status: 'rejected',
      label,
      message:
        code === 'ATTACK_NOT_ENABLED'
          ? ATTACK_OFF_MESSAGE
          : late.reason instanceof Error && late.reason.message
            ? late.reason.message
            : 'Order failed',
    });
    if (code != null) get().noteCapabilityRefusal(code);
  };

  // ── B9: the S hold's receipt check ────────────────────────────────────
  let holdCheckTimer: ReturnType<typeof setTimeout> | null = null;
  let holdCheckRunning = false;
  const stopHoldChecks = (): void => {
    if (holdCheckTimer != null) clearTimeout(holdCheckTimer);
    holdCheckTimer = null;
  };
  const scheduleHoldCheck = (step: number): void => {
    stopHoldChecks();
    const delay = TWO_SIDED_HOLD_CHECK_MS[Math.min(step, TWO_SIDED_HOLD_CHECK_MS.length - 1)];
    holdCheckTimer = setTimeout(() => {
      holdCheckTimer = null;
      void checkHoldReceipts(step + 1);
    }, delay);
  };
  /**
   * Release every held leg whose receipt shows a known state. Filled,
   * cancelled and rejected are terminal; resting and partially filled are
   * known to exist at the venue, which is all the hold was waiting for. An
   * 'unknown' receipt, a missing one or a failed read proves nothing, so the
   * leg stays held and is asked again later.
   */
  const checkHoldReceipts = async (nextStep: number): Promise<void> => {
    const hold = get().twoSidedHold;
    if (hold == null || holdCheckRunning) return;
    holdCheckRunning = true;
    const known = new Set<string>();
    try {
      for (const ticker of new Set(hold.legs.map((leg) => leg.ticker))) {
        const receipts = await _receiptReader(ticker);
        for (const receipt of receipts ?? []) {
          if (receipt.state !== 'unknown') known.add(receipt.clientOrderId);
        }
      }
    } finally {
      holdCheckRunning = false;
    }
    const current = get().twoSidedHold;
    if (current == null) return;
    const remaining = current.legs.filter((leg) => !known.has(leg.clientOrderId));
    if (remaining.length === 0) {
      stopHoldChecks();
      set({ twoSidedHold: null });
      return;
    }
    if (remaining.length !== current.legs.length) {
      set({ twoSidedHold: { ...current, legs: remaining } });
    }
    if (holdCheckTimer == null) scheduleHoldCheck(nextStep);
  };

  return {
  armed: false,
  sizeContracts: loadSize(),
  lastAck: null,
  lastAccepted: null,

  disarmReason: null,
  disarmSeq: 0,
  attackRestore: false,
  deskOff: { twoSided: false, pullAll: false },

  noteCapabilityRefusal: (code) => {
    switch (code) {
      case 'ONE_CLICK_NOT_ENABLED':
      case 'LT2_NOT_ENABLED': {
        const reason =
          code === 'ONE_CLICK_NOT_ENABLED'
            ? 'One-click was turned off for your account'
            : 'Live Trading V2 was turned off for your account';
        if (get().armed) get().disarm(reason);
        // Access loss is not a board change: Attack is not brought back.
        set({ attackRestore: false });
        void refreshProfile();
        return;
      }
      case 'ATTACK_NOT_ENABLED':
        _attackState?.().disarmAttack?.();
        void refreshProfile();
        return;
      case 'LT2_TWO_SIDED_DISABLED':
        set((state) => ({ deskOff: { ...state.deskOff, twoSided: true } }));
        return;
      case 'PULL_ALL_DISABLED':
        set((state) => ({ deskOff: { ...state.deskOff, pullAll: true } }));
        return;
    }
  },
  // D5 — re-arming clears a settled line, never an unresolved or failed one.
  // B6 — attackStore reads `attackRestore` from the previous state on this
  // transition and re-arms Attack; it is spent here.
  arm: () =>
    set((state) => ({
      armed: true,
      disarmReason: null,
      attackRestore: false,
      lastAck: ackSurvivesArm(state.lastAck) ? state.lastAck : null,
    })),
  // D4 — only a MANUAL disarm (the trader's own 1-Click off, or losing
  // one-click access) is a decision about an S hold's lost leg. Every other
  // disarm, with or without a reason, is the board changing under the
  // trader, and the hold survives it.
  disarm: (reason, opts) => {
    if (opts?.manual === true) stopHoldChecks();
    // B6 — read BEFORE the set below: attackStore's subscription drops the
    // Attack arm synchronously on this transition.
    const attackRestore =
      reason != null && opts?.manual !== true && _attackState?.().attackArmed === true;
    // D5 — the line is kept: anything written after this moment is news the
    // status line shows while one-click is off.
    set({
      armed: false,
      disarmReason: reason ?? null,
      disarmSeq: _ackSeq,
      attackRestore,
      ...(opts?.manual === true ? { twoSidedHold: null } : {}),
    });
  },

  twoSidedHold: null,
  twoSidedHoldReason: () => {
    const hold = get().twoSidedHold;
    if (hold == null) return null;
    if (hold.legs.every(twoSidedLegSeen)) {
      stopHoldChecks();
      set({ twoSidedHold: null });
      return null;
    }
    // The trader wants S now: ask the receipts again rather than at the next
    // scheduled check (B9).
    void checkHoldReceipts(1);
    return `held: earlier ${hold.label} side unconfirmed; ${CHECK_OPEN_ORDERS} — S returns once it is confirmed`;
  },

  releaseTwoSidedHold: () => {
    stopHoldChecks();
    set({ twoSidedHold: null });
  },

  rejectLocal: (label: string, message: string) => {
    playLocalRefusalTone();
    startPress({ status: 'rejected', label, message });
  },

  noteAck: (status, label, message, press) => {
    if (status === 'rejected' || status === 'partial') playLocalRefusalTone();
    else if (status === 'pending' && press == null) playLocalAckTone();
    const partial: AckPartial = { status, label, ...(message != null ? { message } : {}) };
    if (press == null) return startPress(partial);
    updatePress(press, partial);
    return press;
  },

  setSizeContracts: (n: number) => {
    const clamped = clampSize(n);
    // Non-finite is refused: the size the trader last set stays live.
    if (clamped == null) return;
    set({ sizeContracts: clamped });
    try {
      localStorage.setItem(SIZE_STORAGE_KEY, String(clamped));
    } catch {
      /* private mode — session-only size is fine */
    }
  },

  submit: async (input: OneClickSubmitInput) => {
    const { armed, sizeContracts } = get();
    if (!armed) return; // click sites gate on armed; belt-and-suspenders
    const count = clampSize(sizeContracts);

    const ack = startPress;

    if (count == null) {
      playLocalRefusalTone();
      ack({ status: 'rejected', label: input.label, message: SIZE_NOT_SET });
      return;
    }
    const ackLabel = sizedAckLabel(input.label, count);

    const priceRefusal = intentPriceRefusal(input.priceCents);
    if (priceRefusal != null) {
      ack({ status: 'rejected', label: ackLabel, message: priceRefusal });
      return;
    }

    // Good to Start: no surface on this legacy input, so the Settings default.
    const duration = resolvePressDuration({ ticker: input.ticker });
    if (!duration.ok) {
      playLocalRefusalTone();
      ack({ status: 'rejected', label: ackLabel, message: duration.message });
      return;
    }

    // Pacing — drop, don't queue. The window is measured from the last
    // ACCEPTED submission attempt (not from throttled drops), so a rapid
    // double-click drops exactly the second click.
    const now = Date.now();
    if (now - _lastSubmitAtMs < MIN_ORDER_SPACING_MS) {
      playLocalRefusalTone();
      ack({ status: 'throttled', label: ackLabel, message: pacingMessage(ackLabel) });
      return;
    }
    _lastSubmitAtMs = now;

    const press = ack({ status: 'pending', label: ackLabel });
    playLocalAckTone();
    try {
      const placed = await useAccountStore.getState().placeOrder({
        ticker: input.ticker,
        side: input.side,
        action: 'buy',
        type: 'limit',
        count,
        price: input.priceCents,
        postOnly: input.postOnly,
        clientOrderId: `lt-${crypto.randomUUID()}`,
        ...expiryFields(duration),
      });
      // #1229: a partial self-trade cancel is accepted-with-a-receipt: the
      // fills are real, nothing rests. A zero-fill cancel threw above.
      updatePress(press, {
        status: 'accepted',
        label: ackLabel,
        ...(placed.selfTradeCancel ? { message: selfTradePartialMessage(placed.selfTradeCancel) } : {}),
      });
    } catch (err) {
      // Same distinction as submitIntent: a lost response is not a rejection.
      updatePress(press, {
        status: err instanceof OrderSubmissionUnknownError ? 'unverified' : 'rejected',
        label: ackLabel,
        message: err instanceof Error && err.message ? err.message : 'Order failed',
      });
    }
  },

  submitIntent: async (
    intent: LiveOrderIntent,
    book: VenueBook | null | undefined,
    report?: PlacementReport,
  ) => {
    const { armed, sizeContracts } = get();
    if (!armed) return; // click sites gate on armed; belt-and-suspenders
    // Frozen at entry: the size this call will send, and the identity every
    // ack on this call carries. The size input may change while the request
    // is in flight; nothing below reads it again.
    const count = clampSize(sizeContracts);

    // A refusal or the pending line starts this press's line; its results
    // follow through updatePress (D13).
    const ack = startPress;

    if (count == null) {
      playLocalRefusalTone();
      ack({ status: 'rejected', label: intent.label, message: SIZE_NOT_SET });
      return;
    }
    const ackLabel = sizedAckLabel(intent.label, count);

    // SUBMIT-TIME guard — the render-time pill disable is presentation, this
    // is the enforcement. Refusals are truthful acks, never silent drops.
    const guardAt = lt2ClockStamp();
    const guard = evaluateIntentGuard(intent, book);
    // Good to Start: the duration the trader sees, or a named refusal.
    const duration = guard.ok ? resolvePressDuration(intent) : null;
    if (!guard.ok || duration == null || !duration.ok) {
      playLocalRefusalTone();
      ack({ status: 'rejected', label: ackLabel, message: refusalMessage(guard, duration) });
      const refusedAt = lt2ClockStamp();
      recordLt2Timing({
        inputMethod: intent.inputMethod,
        outcome: 'guard-refused',
        inputAt: intent.inputAtClock ?? null,
        guard: guardAt,
        ack: refusedAt,
        request: null,
        final: refusedAt,
        finalOutcome: 'terminal-at-ack',
      });
      return;
    }

    // Pacing — drop, don't queue (same window as `submit`).
    const now = Date.now();
    if (now - _lastSubmitAtMs < MIN_ORDER_SPACING_MS) {
      playLocalRefusalTone();
      ack({ status: 'throttled', label: ackLabel, message: pacingMessage(ackLabel) });
      const throttledAt = lt2ClockStamp();
      recordLt2Timing({
        inputMethod: intent.inputMethod,
        outcome: 'throttled',
        inputAt: intent.inputAtClock ?? null,
        guard: guardAt,
        ack: throttledAt,
        request: null,
        final: throttledAt,
        finalOutcome: 'terminal-at-ack',
      });
      return;
    }
    _lastSubmitAtMs = now;

    // Coid minted BEFORE the call so the audit stamp and the reconciliation
    // anchor share it even if the response is lost mid-flight.
    const clientOrderId = `lt-${crypto.randomUUID()}`;
    const press = ack({ status: 'pending', label: ackLabel });
    const scope = pressScope();
    // LOCAL acknowledgement — before the await, so the sound is the keypress's
    // echo, not the venue's (parity contract; venue truth follows separately).
    playLocalAckTone();
    const ackAt = lt2ClockStamp();
    const requestAt = lt2ClockStamp();
    // The timing entry is created NOW, at the local pending acknowledgement,
    // so its render stamp measures the first frame after THIS ack — the
    // input→first-render number the parity review needs — not the first
    // frame after the venue answered. Outcome and response are patched onto
    // this same entry when the relay replies.
    const timingId = recordLt2Timing({
      inputMethod: intent.inputMethod,
      outcome: 'pending',
      inputAt: intent.inputAtClock ?? null,
      guard: guardAt,
      ack: ackAt,
      request: requestAt,
    });
    try {
      const placed = await placeWithDeadline(useAccountStore.getState().placeOrder({
        ticker: intent.ticker,
        side: intent.side,
        action: 'buy',
        type: 'limit',
        count,
        price: intent.priceCents,
        postOnly: intent.postOnly,
        clientOrderId,
        ...expiryFields(duration),
        // V1 moneyline and N-outcome moneyline CLICKS also use submitIntent
        // for its stronger submit-time guard, but must not acquire the
        // separate LT2 capability boundary merely because the implementation
        // is shared. HOTKEY intents are that capability, so they are stamped
        // on every market kind (#647) — see lt2AuditRequired.
        auditMeta: lt2AuditRequired(intent)
          ? buildLt2AuditMeta(intent, guard, book)
          : undefined,
      }), (late) => noteLateResult(press, scope, ackLabel, intent, clientOrderId, count, late));
      const responseAt = lt2ClockStamp();
      const venueOrderId =
        placed != null && typeof placed.orderId === 'string' && placed.orderId !== ''
          ? placed.orderId
          : null;
      // LT2-009 reconciliation below decides the RESTING order's terminal
      // moment; a crossing (taker) or id-less acceptance is terminal at the
      // response itself.
      report?.placed(venueOrderId, intent.ticker);
      const expectRest = intent.postOnly || guard.role !== 'taker';
      // #1229: after a self-trade cancel nothing rests, whatever the guard
      // expected; the fills that landed are the whole outcome.
      const verifies = venueOrderId != null && expectRest && placed?.selfTradeCancel == null;
      updateLt2Timing(timingId, {
        outcome: 'accepted',
        response: responseAt,
        ...(verifies ? {} : { final: responseAt, finalOutcome: 'accepted-terminal' }),
      });
      const acceptedMessage = placed?.selfTradeCancel ? selfTradePartialMessage(placed.selfTradeCancel) : undefined;
      // #1278: answered after a sign-in or account change, the order is the
      // old session's; nothing of it reaches the new session's strip.
      if (pressScopeCurrent(scope)) {
        updatePress(press, {
          status: 'accepted',
          label: ackLabel,
          ...(acceptedMessage != null ? { message: acceptedMessage } : {}),
        });
        if (venueOrderId != null) {
          watchFills(press, ackLabel, venueOrderId, intent.side, count, acceptedMessage, placed?.selfTradeCancel?.filled);
        }
        set({
          lastAccepted: {
            clientOrderId,
            venueOrderId,
            ticker: intent.ticker,
            side: intent.side,
            priceCents: intent.priceCents,
            atMs: Date.now(),
          },
        });
      } else {
        dropPendingLine(press);
      }
      // LT2-009 reconciliation: a maker order the venue accepted should be
      // visible among open orders (placeOrder inserts confirmed resting
      // orders synchronously and refreshOrders re-reads). If it has vanished
      // shortly after acceptance without a newer ack, say so explicitly
      // instead of letting the trader assume it rests. Crossing intents
      // (role 'taker', non-postOnly) legitimately leave the book — skip.
      if (verifies) {
        setTimeout(() => {
          // D13 — the question is asked whatever the strip shows now: an
          // order that is not on the book is news even after a newer press.
          // Resting is visible; a fill is also a fully accounted-for exit —
          // only authoritative evidence counts (see restingOrderVisible).
          const visible = restingOrderVisible(venueOrderId);
          if (!visible && pressScopeCurrent(scope)) {
            playLocalRefusalTone();
            updatePress(press, { status: 'unverified', label: ackLabel, message: NOT_ON_BOOK_MESSAGE });
          }
          updateLt2Timing(timingId, {
            final: lt2ClockStamp(),
            finalOutcome: visible ? 'resting-verified' : 'unverified',
            ...(visible ? {} : { outcome: 'unverified' as const }),
          });
        }, ONE_CLICK_VERIFY_AFTER_MS);
      }
    } catch (err) {
      const outcome = err instanceof OrderSubmissionUnknownError ? 'unverified' : 'rejected';
      if (outcome === 'unverified') report?.lost();
      const inSession = pressScopeCurrent(scope);
      if (inSession) {
        updatePress(press, {
          status: outcome,
          label: ackLabel,
          message: err instanceof Error && err.message ? err.message : 'Order failed',
        });
      } else {
        dropPendingLine(press);
      }
      const failAt = lt2ClockStamp();
      updateLt2Timing(timingId, { outcome, response: failAt, final: failAt, finalOutcome: outcome });
      // D15 — after the line is written, so a disarm keeps it as its own.
      const code = outcome === 'rejected' ? refusalCode(err) : undefined;
      if (code != null && inSession) get().noteCapabilityRefusal(code);
    }
  },

  submitAttackIntent: async (
    intent: LiveOrderIntent,
    book: VenueBook | null | undefined,
    report?: PlacementReport,
  ) => {
    const { armed } = get();
    const attackState = _attackState?.() ?? null;
    // The listener gates on both arms; belt-and-suspenders here so no other
    // caller can fire an attack from a half-armed (or not-even-loaded) state.
    if (!armed || attackState == null || !attackState.attackArmed) return;
    const { attackSizeContracts } = attackState;

    // A refusal or the pending line starts this press's line; its results
    // follow through updatePress (D13).
    const ack = startPress;

    // No usable attack size sends nothing; it is never replaced by a default.
    if (!Number.isFinite(attackSizeContracts)) {
      playLocalRefusalTone();
      ack({ status: 'rejected', label: intent.label, message: 'Attack size is not set — nothing sent' });
      return;
    }

    // An attack intent must carry its own provenance — a missing payload is a
    // caller bug, refused rather than silently downgraded to a passive order.
    if (intent.attack == null || intent.postOnly) {
      playLocalRefusalTone();
      ack({ status: 'rejected', label: intent.label, message: 'Malformed attack intent' });
      return;
    }
    const guardAt = lt2ClockStamp();
    // ACTION-TIME PROOF: re-derive the bounded target from the book handed in
    // at THIS boundary, and refuse if it no longer matches what the keypress
    // computed. The listener's derivation is at most one render frame older,
    // but the packet's contract is a fresh snapshot at action time — recompute
    // OR block, never a stale bound. Blocking is the safe half: submitting a
    // silently recomputed price the trader never saw acknowledged would trade
    // one staleness for another.
    const freshTarget = deriveAttackTarget(book ?? null, intent.side, false);
    if (
      freshTarget == null ||
      freshTarget.targetPriceCents !== intent.priceCents ||
      freshTarget.referencePriceCents !== intent.attack.referencePriceCents ||
      freshTarget.depth !== intent.attack.depth
    ) {
      playLocalRefusalTone();
      ack({
        status: 'rejected',
        label: intent.label,
        message: 'Book changed — attack not sent; re-press at the new level',
      });
      const refusedAt = lt2ClockStamp();
      recordLt2Timing({
        inputMethod: intent.inputMethod,
        outcome: 'guard-refused',
        inputAt: intent.inputAtClock ?? null,
        guard: guardAt,
        ack: refusedAt,
        request: null,
        final: refusedAt,
        finalOutcome: 'terminal-at-ack',
      });
      return;
    }

    // Same submit-time guard as every other intent: identity, availability,
    // price range, and role. postOnly is false, so an intentional taker role
    // passes — the aggression bound lives in the resolver-computed target
    // (reference + accepted depth) and the relay's attack ceiling.
    const guard = evaluateIntentGuard(intent, book);
    // Good to Start applies to Attack exactly as to any other press.
    const duration = guard.ok ? resolvePressDuration(intent) : null;
    if (!guard.ok || duration == null || !duration.ok) {
      playLocalRefusalTone();
      ack({ status: 'rejected', label: intent.label, message: refusalMessage(guard, duration) });
      const refusedAt = lt2ClockStamp();
      recordLt2Timing({
        inputMethod: intent.inputMethod,
        outcome: 'guard-refused',
        inputAt: intent.inputAtClock ?? null,
        guard: guardAt,
        ack: refusedAt,
        request: null,
        final: refusedAt,
        finalOutcome: 'terminal-at-ack',
      });
      return;
    }

    const now = Date.now();
    if (now - _lastSubmitAtMs < MIN_ORDER_SPACING_MS) {
      playLocalRefusalTone();
      ack({ status: 'throttled', label: intent.label, message: pacingMessage(intent.label) });
      const throttledAt = lt2ClockStamp();
      recordLt2Timing({
        inputMethod: intent.inputMethod,
        outcome: 'throttled',
        inputAt: intent.inputAtClock ?? null,
        guard: guardAt,
        ack: throttledAt,
        request: null,
        final: throttledAt,
        finalOutcome: 'terminal-at-ack',
      });
      return;
    }
    _lastSubmitAtMs = now;

    // The ATTACK size, never the one-click size. The Attack store normalizes
    // persisted values; the relay enforces the current per-trader ceiling.
    const count = Math.max(1, Math.round(attackSizeContracts));
    const clientOrderId = `lt-${crypto.randomUUID()}`;
    const press = ack({ status: 'pending', label: intent.label });
    const scope = pressScope();
    playLocalAckTone();
    const ackAt = lt2ClockStamp();
    const requestAt = lt2ClockStamp();
    // Same contract as submitIntent: the entry exists from the local pending
    // acknowledgement onward, so renderMs measures the ack frame, never the
    // response frame.
    const timingId = recordLt2Timing({
      inputMethod: intent.inputMethod,
      outcome: 'pending',
      inputAt: intent.inputAtClock ?? null,
      guard: guardAt,
      ack: ackAt,
      request: requestAt,
    });
    try {
      const placed = await placeWithDeadline(useAccountStore.getState().placeOrder({
        ticker: intent.ticker,
        side: intent.side,
        action: 'buy',
        type: 'limit',
        count,
        price: intent.priceCents,
        postOnly: false,
        clientOrderId,
        ...expiryFields(duration),
        // Every hotkey intent is LT2-stamped (lt2AuditRequired); the attack
        // marker is ADDITIONAL — the relay's gate input and the durable
        // record of reference/depth this bounded action was computed from.
        auditMeta: buildLt2AuditMeta(intent, guard, book),
        // The durable action-time evidence: the marker the relay's arithmetic
        // gate checks, plus the minimal exact book snapshot the target was
        // proven against — both raw sides' top levels, so a reader can
        // reconstruct the chosen side's reference, the target, and the
        // opposing liquidity at it without trusting any derived field.
        attackMeta: {
          depth: intent.attack.depth,
          referencePriceCents: intent.attack.referencePriceCents,
          book: {
            atMs: Date.now(),
            marketTicker: book?.marketTicker ?? null,
            freshness: book?.freshness ?? null,
            lastPriceCents: book?.lastPriceCents ?? null,
            yes: (book?.yes ?? []).slice(0, 3).map((l) => ({ p: l.priceCents, s: l.size })),
            no: (book?.no ?? []).slice(0, 3).map((l) => ({ p: l.priceCents, s: l.size })),
          },
        },
      }), (late) => noteLateResult(press, scope, intent.label, intent, clientOrderId, count, late));
      const responseAt = lt2ClockStamp();
      const venueOrderId =
        placed != null && typeof placed.orderId === 'string' && placed.orderId !== ''
          ? placed.orderId
          : null;
      // LT2-009 reconciliation applies to the ESTABLISH half only: a taker
      // fill legitimately never rests. guard.role is 'taker' when displayed
      // opposing liquidity crossed the target — skip; maker/unknown means the
      // order should be visible resting at the target.
      report?.placed(venueOrderId, intent.ticker);
      const expectRest = guard.role !== 'taker';
      const verifies = venueOrderId != null && expectRest && placed?.selfTradeCancel == null;
      updateLt2Timing(timingId, {
        outcome: 'accepted',
        response: responseAt,
        ...(verifies ? {} : { final: responseAt, finalOutcome: 'accepted-terminal' }),
      });
      const acceptedMessage = placed?.selfTradeCancel ? selfTradePartialMessage(placed.selfTradeCancel) : undefined;
      // #1278: answered after a sign-in or account change, the order is the
      // old session's; nothing of it reaches the new session's strip.
      if (pressScopeCurrent(scope)) {
        updatePress(press, {
          status: 'accepted',
          label: intent.label,
          ...(acceptedMessage != null ? { message: acceptedMessage } : {}),
        });
        if (venueOrderId != null) {
          watchFills(press, intent.label, venueOrderId, intent.side, count, acceptedMessage, placed?.selfTradeCancel?.filled);
        }
        set({
          lastAccepted: {
            clientOrderId,
            venueOrderId,
            ticker: intent.ticker,
            side: intent.side,
            priceCents: intent.priceCents,
            atMs: Date.now(),
          },
        });
      } else {
        dropPendingLine(press);
      }
      if (verifies) {
        setTimeout(() => {
          // D13 — asked whatever the strip shows now (as submitIntent).
          const visible = restingOrderVisible(venueOrderId);
          if (!visible && pressScopeCurrent(scope)) {
            playLocalRefusalTone();
            updatePress(press, { status: 'unverified', label: intent.label, message: NOT_ON_BOOK_MESSAGE });
          }
          updateLt2Timing(timingId, {
            final: lt2ClockStamp(),
            finalOutcome: visible ? 'resting-verified' : 'unverified',
            ...(visible ? {} : { outcome: 'unverified' as const }),
          });
        }, ONE_CLICK_VERIFY_AFTER_MS);
      }
    } catch (err) {
      const outcome = err instanceof OrderSubmissionUnknownError ? 'unverified' : 'rejected';
      if (outcome === 'unverified') report?.lost();
      const code = outcome === 'rejected' ? refusalCode(err) : undefined;
      const inSession = pressScopeCurrent(scope);
      if (inSession) {
        updatePress(press, {
          status: outcome,
          label: intent.label,
          message:
            code === 'ATTACK_NOT_ENABLED'
              ? ATTACK_OFF_MESSAGE
              : err instanceof Error && err.message
                ? err.message
                : 'Order failed',
        });
      } else {
        dropPendingLine(press);
      }
      const failAt = lt2ClockStamp();
      updateLt2Timing(timingId, { outcome, response: failAt, final: failAt, finalOutcome: outcome });
      if (code != null && inSession) get().noteCapabilityRefusal(code);
    }
  },

  submitTwoSidedIntent: async ({ label, legs, books, report, sizeMultiplier, ceilingContracts }) => {
    const { armed, sizeContracts } = get();
    if (!armed) return; // the listener gates on armed; belt-and-suspenders
    // Frozen at entry, shared by both legs: one press, one size. The base is
    // the S size exactly as before; Shift+S multiplies it here, ONCE, before
    // any guard or relay check sees the quantity (#1067).
    const sizedBase = clampSize(sizeContracts);

    // A refusal or the pending line starts this press's line; its results
    // follow through updatePress (D13).
    const ack = startPress;
    const refuse = (message: string, legAcks: OneClickLegAck[]): void => {
      playLocalRefusalTone();
      ack({ status: 'rejected', label, message, legs: legAcks });
    };

    if (sizedBase == null) {
      refuse(SIZE_NOT_SET, [
        { label: legs[0].label, status: 'rejected', message: 'not sent' },
        { label: legs[1].label, status: 'rejected', message: 'not sent' },
      ]);
      return;
    }
    const baseCount = sizedBase;
    const count = sizeMultiplier == null ? baseCount : baseCount * sizeMultiplier;
    const legLabels = legs.map((leg) => sizedAckLabel(leg.label, count));
    const refusedTiming = (leg: LiveOrderIntent, guardAt: ReturnType<typeof lt2ClockStamp>) => {
      const refusedAt = lt2ClockStamp();
      recordLt2Timing({
        inputMethod: leg.inputMethod,
        outcome: 'guard-refused',
        inputAt: leg.inputAtClock ?? null,
        guard: guardAt,
        ack: refusedAt,
        request: null,
        final: refusedAt,
        finalOutcome: 'terminal-at-ack',
      });
    };

    // A two-sided press is PASSIVE by definition (#724: "the two passive
    // top-of-book postings that correspond to A and D"). A crossing leg would
    // pay the spread the pair exists to capture, and the listener already
    // refuses it; this is the boundary's own assertion, not a downgrade.
    const guardAt = lt2ClockStamp();
    if (legs.some((leg) => !leg.postOnly || leg.attack != null)) {
      refuse('S posts both sides passively — switch the card to maker pricing', [
        { label: legLabels[0], status: 'rejected', message: 'not sent' },
        { label: legLabels[1], status: 'rejected', message: 'not sent' },
      ]);
      legs.forEach((leg) => refusedTiming(leg, guardAt));
      return;
    }

    // #1067 — the multiplied quantity is checked, never adjusted. A clamp
    // would send a size the trader did not choose; a refusal names the number
    // so they can fix the setting or the base size.
    if (sizeMultiplier != null) {
      // No ceiling given reads as the conservative fallback, never as "none".
      const ceiling = ceilingContracts ?? ONE_CLICK_FALLBACK_MAX_CONTRACTS;
      // Ceiling before the whole-count check, so an absurd multiplier gets the
      // message the trader can act on rather than an overflow complaint.
      const reason = !Number.isSafeInteger(sizeMultiplier) || sizeMultiplier < 2
        ? `size multiplier ${sizeMultiplier}× is not a whole number 2 or more`
        : count > ceiling
          ? `${sizeMultiplier}× of ${baseCount} = ${count} per side exceeds your ${ceiling} one-click limit`
          : !Number.isSafeInteger(count)
            ? `${sizeMultiplier}× of ${baseCount} is not a whole contract count`
            : null;
      if (reason != null) {
        refuse(`${reason} — nothing sent`, [
          { label: legLabels[0], status: 'rejected', message: 'not sent' },
          { label: legLabels[1], status: 'rejected', message: 'not sent' },
        ]);
        legs.forEach((leg) => refusedTiming(leg, guardAt));
        return;
      }
    }

    // ALL-OR-NONE PREFLIGHT. Both legs are guarded against their current books
    // before either is sent; one refusal refuses the press. This is the only
    // place a two-sided press is atomic — everything after the wire is not.
    // The live source check `placeOrder` makes at send time is asked here
    // too, for BOTH legs: the card's books can be a frame older than the
    // market store, and a leg that placeOrder would refuse must refuse the
    // press, not become a one-sided order.
    // Good to Start is asked per leg in the same preflight: a leg whose
    // duration cannot be honored refuses the press like any other refusal.
    const durations: OneClickDuration[] = [];
    const guards = legs.map((leg, i): ReturnType<typeof evaluateIntentGuard> | { ok: false; message: string } => {
      const guard = evaluateIntentGuard(leg, books[i]);
      if (!guard.ok) return guard;
      const source = sourceConfirmationError({ ticker: leg.ticker });
      if (source != null) {
        return { ok: false, code: 'stale', message: 'Source confirmation unavailable — order not sent' };
      }
      const duration = resolvePressDuration(leg);
      durations[i] = duration;
      return duration.ok ? guard : { ok: false, message: duration.message };
    });
    const failed = guards.findIndex((g) => !g.ok);
    if (failed !== -1) {
      const legAcks = guards.map((g, i): OneClickLegAck =>
        g.ok
          ? { label: legLabels[i], status: 'rejected', message: 'not sent — other side refused' }
          : { label: legLabels[i], status: 'rejected', message: g.message },
      );
      // The summary names the side; the leg line beneath it carries the
      // reason, once. Repeating the reason on both lines doubled the strip's
      // height for no extra information (visual pass, Sep 22).
      refuse(`${legs[failed].label} refused — nothing sent`, legAcks);
      legs.forEach((leg) => refusedTiming(leg, guardAt));
      return;
    }
    const okGuards = guards.map((g) => {
      if (!g.ok) throw new Error('unreachable: preflight failure escaped');
      return g;
    });

    // Pacing — ONE window per press, not per leg. Measured like every other
    // submission; the next press (any key) is paced from this stamp.
    const now = Date.now();
    if (now - _lastSubmitAtMs < MIN_ORDER_SPACING_MS) {
      playLocalRefusalTone();
      ack({ status: 'throttled', label, message: pacingMessage(label) });
      const throttledAt = lt2ClockStamp();
      legs.forEach((leg) =>
        recordLt2Timing({
          inputMethod: leg.inputMethod,
          outcome: 'throttled',
          inputAt: leg.inputAtClock ?? null,
          guard: guardAt,
          ack: throttledAt,
          request: null,
          final: throttledAt,
          finalOutcome: 'terminal-at-ack',
        }),
      );
      return;
    }
    _lastSubmitAtMs = now;

    // Identities minted BEFORE either call: one group id shared by both legs,
    // one `lt-` coid each, so the audit rows can be joined back into the pair
    // even if a response is lost mid-flight.
    const groupId = crypto.randomUUID();
    const clientOrderIds = legs.map(() => `lt-${crypto.randomUUID()}`);
    const stamped = legs.map(
      (leg, i): LiveOrderIntent => ({
        ...leg,
        group: { id: groupId, mode: 'two-sided', leg: i, of: legs.length },
        // The durable record of a Shift+S press: without it a 300-lot S leg
        // reads the same as a 3× Shift+S leg of a 100 one-click size.
        ...(sizeMultiplier != null ? { sizeMultiplier } : {}),
      }),
    );

    // The group is ONE press (D13): its own later states replace its line, and
    // once anything newer has spoken they are added beneath that instead.
    const groupPress = ack({
      status: 'pending',
      label,
      legs: legLabels.map((l): OneClickLegAck => ({ label: l, status: 'pending' })),
    });
    // LOCAL acknowledgement — once, before the awaits: the keypress's echo.
    playLocalAckTone();
    const ackAt = lt2ClockStamp();
    const requestAt = lt2ClockStamp();
    const timingIds = stamped.map((leg) =>
      recordLt2Timing({
        inputMethod: leg.inputMethod,
        outcome: 'pending',
        inputAt: leg.inputAtClock ?? null,
        guard: guardAt,
        ack: ackAt,
        request: requestAt,
      }),
    );

    // CONCURRENT placement — "both sides at once" is the request. Each leg is
    // the ordinary audited placeOrder call submitIntent makes, with the same
    // size and the same audit builder; only the group stamp is new.
    // Each leg's response is stamped when THAT leg settles. The relay admits
    // the two one at a time under its per-user order lock, so the second
    // leg's response is later than the first's; one shared stamp taken after
    // both would hide that in the timing record.
    const responseAts: Array<ReturnType<typeof lt2ClockStamp>> = [];
    // D15 — capability codes the legs were refused with, acted on once the
    // press's line is written.
    const legCodes = new Set<string>();
    // B3 — a leg's answer after its deadline is applied once the press's line
    // is written (below); until then it waits here.
    let applyLateLeg: ((i: number, late: PromiseSettledResult<unknown>) => void) | null = null;
    const lateLegs: Array<[number, PromiseSettledResult<unknown>]> = [];
    const settled = await Promise.allSettled(
      stamped.map((leg, i) =>
        placeWithDeadline(useAccountStore.getState().placeOrder({
          ticker: leg.ticker,
          side: leg.side,
          action: 'buy',
          type: 'limit',
          count,
          price: leg.priceCents,
          postOnly: leg.postOnly,
          clientOrderId: clientOrderIds[i],
          ...expiryFields(durations[i]),
          // Every hotkey intent is LT2-stamped (lt2AuditRequired); the group
          // stamp rides inside the same durable record.
          auditMeta: buildLt2AuditMeta(leg, okGuards[i], books[i]),
        }).finally(() => {
          responseAts[i] = lt2ClockStamp();
        }), (late) => {
          if (applyLateLeg != null) applyLateLeg(i, late);
          else lateLegs.push([i, late]);
        }),
      ),
    );
    const results = settled.map((outcome, i): TwoSidedLegResult => {
      const responseAt = responseAts[i] ?? lt2ClockStamp();
      if (outcome.status === 'fulfilled') {
        const placed = outcome.value;
        const venueOrderId =
          placed != null && typeof placed.orderId === 'string' && placed.orderId !== ''
            ? placed.orderId
            : null;
        updateLt2Timing(timingIds[i], {
          outcome: 'accepted',
          response: responseAt,
          ...(venueOrderId == null ? { final: responseAt, finalOutcome: 'accepted-terminal' } : {}),
        });
        return {
          status: 'accepted',
          venueOrderId,
          ...(placed?.selfTradeCancel ? { message: selfTradePartialMessage(placed.selfTradeCancel) } : {}),
        };
      }
      const err: unknown = outcome.reason;
      const status = err instanceof OrderSubmissionUnknownError ? 'unverified' : 'rejected';
      const message = err instanceof Error && err.message ? err.message : 'Order failed';
      if (status === 'rejected') {
        const code = refusalCode(err);
        if (code != null) legCodes.add(code);
      }
      updateLt2Timing(timingIds[i], {
        outcome: status,
        response: responseAt,
        final: responseAt,
        finalOutcome: status,
      });
      return { status, message };
    });

    const legAcks = results.map(
      (r, i): OneClickLegAck =>
        r.status === 'accepted'
          ? { label: legLabels[i], status: 'accepted', ...(r.message ? { message: r.message } : {}) }
          : { label: legLabels[i], status: r.status, message: r.message },
    );
    results.forEach((r, i) => {
      if (r.status === 'accepted') report?.placed(r.venueOrderId, legs[i].ticker);
      else if (r.status === 'unverified') report?.lost();
    });
    // A lost response holds S until that side is seen (or the trader resets).
    const lostLegs = results.flatMap((r, i) =>
      r.status === 'unverified'
        ? [{ label: legLabels[i], clientOrderId: clientOrderIds[i], ticker: legs[i].ticker }]
        : [],
    );
    if (lostLegs.length > 0) {
      set({ twoSidedHold: { label, legs: lostLegs } });
      scheduleHoldCheck(0);
    }
    // The group's own leg lines, kept here so a later per-leg flip rewrites
    // exactly these and never an earlier press's line added beneath them.
    const groupLegs = [...legAcks];
    // One sentence for the PRESS; each leg line beneath it carries its own
    // reason exactly once, so a refusal is never printed twice.
    const writeGroup = (): void => {
      const now = summarizeTwoSided(results);
      const unknownLeg = results.findIndex((r) => r.status === 'unverified');
      updatePress(groupPress, {
        status: now,
        label,
        ...(now === 'accepted'
          ? {}
          : {
              message:
                now === 'partial'
                  ? 'one side not placed'
                  : now === 'unverified'
                    ? `${legs[unknownLeg].label} — response lost; ${CHECK_OPEN_ORDERS}`
                    : 'neither side placed',
            }),
        legs: [...groupLegs],
      });
    };
    writeGroup();
    legCodes.forEach((code) => get().noteCapabilityRefusal(code));
    // B3 — a leg that answered after its deadline: its line and the press's
    // summary are rewritten with the real answer, and a definite answer ends
    // that leg's S hold (its outcome is no longer unknown).
    applyLateLeg = (i, late) => {
      if (late.status === 'rejected' && late.reason instanceof OrderSubmissionUnknownError) return;
      if (late.status === 'fulfilled') {
        const placed = late.value as { orderId?: unknown } | null | undefined;
        const venueOrderId =
          typeof placed?.orderId === 'string' && placed.orderId !== '' ? placed.orderId : null;
        results[i] = { status: 'accepted', venueOrderId };
        groupLegs[i] = { label: legLabels[i], status: 'accepted' };
      } else {
        const message =
          late.reason instanceof Error && late.reason.message ? late.reason.message : 'Order failed';
        results[i] = { status: 'rejected', message };
        groupLegs[i] = { label: legLabels[i], status: 'rejected', message };
      }
      const hold = get().twoSidedHold;
      if (hold != null) {
        const remaining = hold.legs.filter((leg) => leg.clientOrderId !== clientOrderIds[i]);
        if (remaining.length === 0) {
          stopHoldChecks();
          set({ twoSidedHold: null });
        } else if (remaining.length !== hold.legs.length) {
          set({ twoSidedHold: { ...hold, legs: remaining } });
        }
      }
      writeGroup();
      const code = late.status === 'rejected' ? refusalCode(late.reason) : undefined;
      if (code != null) get().noteCapabilityRefusal(code);
    };
    lateLegs.splice(0).forEach(([i, late]) => applyLateLeg?.(i, late));
    // The reconciliation anchor is the LAST accepted leg in declared order —
    // one anchor, as before; both legs are on the receipts by their coids.
    results.forEach((r, i) => {
      if (r.status !== 'accepted') return;
      set({
        lastAccepted: {
          clientOrderId: clientOrderIds[i],
          venueOrderId: r.venueOrderId,
          ticker: legs[i].ticker,
          side: legs[i].side,
          priceCents: legs[i].priceCents,
          atMs: Date.now(),
        },
      });
    });

    // LT2-009 per accepted leg: every S leg is post-only, so every accepted
    // leg with a venue id is expected to REST and is checked for visibility.
    results.forEach((r, i) => {
      if (r.status !== 'accepted' || r.venueOrderId == null) return;
      const venueOrderId = r.venueOrderId;
      setTimeout(() => {
        // D13 — asked whatever the strip shows now; a newer line gets this
        // leg's news added beneath it.
        const visible = restingOrderVisible(venueOrderId);
        if (!visible) {
          groupLegs[i] = { ...groupLegs[i], status: 'unverified', message: 'not visible on the book' };
          playLocalRefusalTone();
          updatePress(groupPress, {
            status: 'unverified',
            label,
            message: `${legs[i].label} accepted but not on the book — ${CHECK_OPEN_ORDERS}`,
            legs: [...groupLegs],
          });
        }
        updateLt2Timing(timingIds[i], {
          final: lt2ClockStamp(),
          finalOutcome: visible ? 'resting-verified' : 'unverified',
          ...(visible ? {} : { outcome: 'unverified' as const }),
        });
      }, ONE_CLICK_VERIFY_AFTER_MS);
    });
  },
  };
});
